import { useEffect, useState } from 'react'
import { IconArrowRight, IconScale } from '@tabler/icons-react'
import { getSetupAttention, type SetupAttention } from '../../../lib/api'
import { onConfigAttentionChanged } from '../../../lib/attentionSignal'
import { closeNoticeForSession, isNoticeHidden } from '../../../lib/dismissedNotices'
import { useI18n } from '../../../lib/i18n'
import { MascotOutlinePattern } from '../../MascotOutlinePattern'

/**
 * «Tus clientes pagan sin poder leer tus condiciones».
 *
 * Junto al botón de pagar del sitio va «Al continuar al pago aceptas…» con los
 * documentos PUBLICADOS (ver `checkoutLegal.ts` en el worker). Si faltan los
 * términos o los reembolsos, la venta sigue pero sin condiciones aceptadas —y
 * es justo lo que hace falta enseñar el día que alguien reclama un cargo—.
 * La pastilla de Configuración ya cuenta los huecos; esto lo dice donde se
 * vende, con nombre y apellido de lo que falta.
 *
 * Sólo con algo a la venta (el servidor devuelve la lista vacía si no) y sólo
 * se cierra por sesión: es una obligación del negocio, no una sugerencia.
 */
export function LegalAttentionBanner({ projectId, token, onOpenPolicies }: {
  projectId: string
  token: string
  onOpenPolicies: () => void
}) {
  const { t } = useI18n()
  const [missing, setMissing] = useState<NonNullable<SetupAttention['policies']['missingForSale']>>([])
  const [hidden, setHidden] = useState(() => isNoticeHidden('legal-attention'))
  const [version, setVersion] = useState(0)

  useEffect(() => onConfigAttentionChanged((changed) => {
    if (changed === projectId) setVersion((value) => value + 1)
  }), [projectId])

  useEffect(() => {
    let alive = true
    getSetupAttention(token, projectId)
      .then((data) => { if (alive) setMissing(data.sells ? data.policies.missingForSale ?? [] : []) })
      /* Sin respuesta no se afirma nada: mejor sin aviso que con uno falso. */
      .catch(() => {})
    return () => { alive = false }
  }, [projectId, token, version])

  if (hidden || !missing.length) return null

  return (
    <div className="dash-invite-banners">
      <section className="dash-invite-banner dash-loaded-content-in">
        <MascotOutlinePattern className="dash-invite-banner-pattern" eyes={false} />
        <div className="dash-invite-banner-copy">
          <span className="dash-invite-banner-kicker">
            <IconScale size={13} aria-hidden="true" />
            {t('legalAttention.kicker')}
          </span>
          <strong>{t('legalAttention.title')}</strong>
          <p>{t('legalAttention.body', { docs: missing.map((row) => row.title).join(', ') })}</p>
        </div>
        <div className="dash-invite-banner-actions">
          <button
            type="button"
            className="dash-invite-banner-btn"
            onClick={() => { closeNoticeForSession('legal-attention'); setHidden(true) }}
          >
            {t('stripeAttention.close')}
          </button>
          <button type="button" className="dash-invite-banner-btn is-primary" onClick={onOpenPolicies}>
            {t('legalAttention.cta')} <IconArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  )
}
